// src/pages/auth/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  useTheme,
  alpha,
  keyframes,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logo_white.png';
import loginBackground from '../../assets/login_background.png';

// Type for the form data
type LoginFormData = {
  username: string;
  password: string;
};

// Keyframes for animations
const glowAnimation = keyframes`
  0% { box-shadow: 0 0 10px rgba(255, 59, 48, 0.5); }
  50% { box-shadow: 0 0 25px rgba(255, 59, 48, 0.8); }
  100% { box-shadow: 0 0 10px rgba(255, 59, 48, 0.5); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

// SVG Code Component for Tech Animation
const TechSVGAnimation = () => (
  <svg 
    width="120" 
    height="120" 
    viewBox="0 0 120 120"
    style={{
      position: 'absolute',
      top: -60,
      right: -30,
      zIndex: 2,
      animation: `${floatAnimation} 6s ease-in-out infinite`,
    }}
  >
    <circle cx="60" cy="60" r="50" fill="none" stroke="#ff3b30" strokeWidth="1" />
    <circle cx="60" cy="60" r="40" fill="none" stroke="#2979ff" strokeWidth="1" opacity="0.8" style={{animation: `${pulse} 3s infinite`}}/>
    <circle cx="60" cy="60" r="30" fill="none" stroke="#7e57c2" strokeWidth="1" />
    <path d="M30,60 L90,60" stroke="#ff3b30" strokeWidth="1" />
    <path d="M60,30 L60,90" stroke="#2979ff" strokeWidth="1" />
    <path d="M40,40 L80,80" stroke="#7e57c2" strokeWidth="1" />
    <path d="M40,80 L80,40" stroke="#ff3b30" strokeWidth="1" />
    <circle cx="60" cy="60" r="5" fill="#2979ff" style={{animation: `${pulse} 2s infinite`}}/>
  </svg>
);

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { username: '', password: '' },
  });

// src/pages/auth/Login.tsx - onSubmit függvény módosítása

const onSubmit = async (data: LoginFormData) => {
  setLoading(true);
  setError(null);
  try {
    const response = await authService.login(data);
    login();
    
    // Generálunk egy app access tokent
    await authService.generateAppToken();
    
    // Ellenőrizzük, hogy van-e redirect paraméter az URL-ben
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    
    // Ha van redirect paraméter, akkor oda irányítunk
    if (redirect) {
      console.log(`Redirecting to: ${redirect}`);
      window.location.href = redirect;
      return;
    }
    
    // Ha a felhasználónak van alapértelmezett alkalmazása, akkor oda irányítjuk
    if (response.user.default_app) {
      let redirectUrl = response.user.default_app;
      if (!redirectUrl.endsWith('/')) {
        redirectUrl += '/';
      }
      console.log(`Redirecting to default app: ${redirectUrl}`);
      window.location.href = redirectUrl;
      return;
    }
    
    navigate('/');
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Bejelentkezési hiba történt');
  } finally {
    setLoading(false);
  }
};

  // Particle effect
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, size: number, speed: number}>>([]);
  
  useEffect(() => {
    const createParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 30; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 3 + 1,
          speed: Math.random() * 0.5 + 0.2
        });
      }
      setParticles(newParticles);
    };
    
    createParticles();
  }, []);


  useEffect(() => {
    // URL paraméterek ellenőrzése
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    
    if (errorParam === 'session_expired') {
      setError('A munkamenet lejárt. Kérjük, jelentkezzen be újra.');
    } else if (errorParam === 'auth_required') {
      setError('Ez a művelet hitelesítést igényel. Kérjük, jelentkezzen be.');
    }
  }, []);


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${loginBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.45,
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`,
          zIndex: -1
        }
      }}
    >
      {/* Particles */}
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: 'absolute',
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: alpha('#ffffff', 0.5),
            borderRadius: '50%',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animation: `${floatAnimation} ${10 / particle.speed}s infinite ease-in-out`,
            zIndex: 1,
          }}
        />
      ))}
      
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              animation: `${floatAnimation} 6s ease-in-out infinite`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 1
            }}
          >
            <img src={logo} alt="TestPulse Logo" style={{ width: 140, marginBottom: 10 }} />
          </Box>
          
          <Typography 
            component="h1" 
            variant="h3" 
            sx={{ 
              color: 'white', 
              mb: 4, 
              fontFamily: "'Montserrat', sans-serif", 
              fontWeight: 700,
              textShadow: '0 0 15px rgba(0,0,0,0.3)',
              letterSpacing: '1px'
            }}
          >
            Test.Pulse
          </Typography>
          
          <Box 
            sx={{
              textAlign: 'center',
              color: 'white',
              mb: 4,
              maxWidth: '90%',
              animation: `${pulse} 4s infinite ease-in-out`,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontStyle: 'italic' }}>
              Ahol az álmok kódot öltenek
            </Typography>
          </Box>
          
          <Paper
            elevation={8}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: 3,
              position: 'relative',
              backdropFilter: 'blur(10px)',
              background: alpha('#ffffff', 0.85),
              animation: `${glowAnimation} 3s infinite`,
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)',
              }
            }}
          >
            {/* Tech SVG Animation */}
            <TechSVGAnimation />
            
            <Typography 
              component="h2" 
              variant="h5" 
              align="center" 
              gutterBottom
              sx={{ 
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                mb: 3,
                color: theme.palette.primary.dark
              }}
            >
              Bejelentkezés
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
              <Controller
                name="username"
                control={control}
                rules={{ required: 'Felhasználónév megadása kötelező' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    id="username"
                    label="Felhasználónév"
                    autoComplete="username"
                    autoFocus
                    error={!!errors.username}
                    helperText={errors.username?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                )}
              />
              
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Jelszó megadása kötelező' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    id="password"
                    label="Jelszó"
                    type="password"
                    autoComplete="current-password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                )}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 4,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                  backgroundSize: '200% 100%',
                  animation: 'gradient 5s ease infinite',
                  boxShadow: `0 8px 25px -5px ${alpha(theme.palette.primary.main, 0.5)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 30px -5px ${alpha(theme.palette.primary.main, 0.6)}`,
                  },
                  '@keyframes gradient': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                }}
              >
                {loading ? 'Belépés folyamatban...' : 'Belépés'}
              </Button>
            </Box>
          </Paper>
          
          <Box
            sx={{
              mt: 4,
              textAlign: 'center',
              maxWidth: '90%',
            }}
          >
            <Typography variant="body2" color="white" sx={{ mb: 1 }}>
              © {new Date().getFullYear()} Nessyfy Kft. Minden jog fenntartva.
            </Typography>
            <Typography variant="caption" color="white" sx={{ opacity: 0.7 }}>
              A TestPulse rendszer kizárólag a Nessyfy Kft. és annak partnerei számára elérhető.
              A rendszerbe történő jogosulatlan belépési kísérlet jogi következményeket vonhat maga után.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;